import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { motion, useInView, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import TemplatesPage from "@/pages/TemplatesPage";
import BlogListPage from "@/pages/BlogListPage";
import BlogPostPage from "@/pages/BlogPostPage";
import Navigation from "@/components/Navigation";
import NewsletterPopup from "@/components/NewsletterPopup";
import SEO from "@/components/SEO";
import {
  Database,
  BarChart3,
  Server,
  CheckCircle,
  ArrowRight,
  Phone,
  FileSpreadsheet,
  Clock,
  Users,
  ShieldCheck,
  Zap,
  Target,
  Award,
  Calendar,
  Package,
  TrendingUp,
  PieChart,
  Warehouse,
  Receipt,
  BadgePercent,
  Cookie,
  XCircle,
  Sparkles } from
"lucide-react";

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/0t2i84sr_Logo.png";
const FOUNDER_IMG = "https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/qmvcj9bs_IMG_20260225_100958_427.jpg";
const HERO_DEMO_VIDEO = "https://customer-assets-0z36b82j.emergentagent.net/job_dataplatform-pro/artifacts/1eykxifj_hero-demo.mp4";

// Report screenshots
const SALGSRAPPORT_IMG = "https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/30ak5px9_Salgsrapport%20-%20Overblik.png";
const FINANSRAPPORT_IMG = "https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/ymtp8x7q_Finansrapport%20Resultat.png";
const LAGERRAPPORT_IMG = "https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/yd626vi5_Lagerrapport%20Lagerv%C3%A6rdi.png";
const LAGERRAPPORT_DODT_IMG = "https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/depddor2_Lagerrapport%20d%C3%B8dt%20lager.png";
const LAGERRAPPORT_OMS_IMG = "https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/4uuwy462_Lagerrapport%20Oms%C3%A6tningshastighed.png";

// Animation variants
const fadeUpVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Section Component with animation
const AnimatedSection = ({ children, className = "", id = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      id={id}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}>

      {children}
    </motion.section>);

};

// Navigation Component is in /components/Navigation.jsx (imported above)

// Hero Section
const HeroSection = () => {
  return (
    <section id="hero" className="hero-gradient min-h-screen flex items-center relative overflow-hidden" data-testid="hero-section">
      <div className="grid-pattern absolute inset-0" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}>

            <p className="text-teal-400 font-manrope font-semibold text-sm tracking-wide uppercase mb-6">
              Business Intelligence · Data Warehouse · Power BI · AI-assisteret udvikling
            </p>
            <h1 className="font-manrope font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
              Automatiser din BI udvikling med{" "}
              <span className="text-teal-400">AI</span>
            </h1>
            <p className="text-slate-300 text-lg sm:text-xl leading-relaxed mb-8 max-w-xl">
              Power BI, data warehouse og AI-assisteret BI. Til fast pris.
              Start gratis med vores Power BI templates.
            </p>
            
            <div className="flex items-center gap-4 mb-8 p-4 bg-white/5 rounded-xl border border-white/10">
              <Phone className="text-teal-400 flex-shrink-0" size={24} />
              <div>
                <p className="text-slate-400 text-sm">Ring til mig direkte</p>
                <a href="tel:+4561699916" className="text-white font-manrope font-bold text-xl hover:text-teal-400 transition-colors">
                  61 69 99 16
                </a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#booking"
                className="btn-primary inline-flex items-center justify-center gap-2 font-semibold px-8 py-4 rounded-lg text-lg"
                data-testid="hero-cta-primary">

                Book et møde
                <ArrowRight size={20} />
              </a>
              <a
                href="#priser"
                className="btn-secondary inline-flex items-center justify-center gap-2 font-semibold px-8 py-4 rounded-lg text-lg"
                data-testid="hero-cta-secondary">

                Se pakker og priser
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:block">

            <div className="relative">
              <div className="absolute -inset-4 bg-teal-500/20 rounded-2xl blur-2xl" />
              <video
                src={HERO_DEMO_VIDEO}
                poster={SALGSRAPPORT_IMG}
                autoPlay
                loop
                muted
                playsInline
                className="relative dashboard-image w-full"
                data-testid="hero-demo-video" />

            </div>
          </motion.div>
        </div>
      </div>
    </section>);

};

// Client logo URLs
const CLIENT_LOGOS = {
  serviceDanmark: "https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/yx0dpokw_service_danmark_logo_200x52.png",
  dena: "https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/i2ydnr6e_dena_a_s_logo.jpg",
  esbjergHavn: "https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/ar2fxcd2_esbjerg_havn_logo_23.jpg",
  ibg: "https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/q2g3lsop_ib_g_jensen_a_s_logo.jpg",
  misRecycling: "https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/kbbgxwzt_mis-logo.png",
  nordicBioscience: "https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/gfcxtu5e_Nordic_Bioscience_Logo.jpg",
  firstFarms: "https://customer-assets.emergentagent.com/job_dataplatform-pro/artifacts/6tvhp30k_image.png"
};

// Client Logos Carousel
const LogoCarousel = () => {
  const clients = [
    { name: "Service Danmark", logo: CLIENT_LOGOS.serviceDanmark, scale: 1 },
    { name: "Esbjerg Havn", logo: CLIENT_LOGOS.esbjergHavn, scale: 2 },
    { name: "IBG A/S", logo: CLIENT_LOGOS.ibg, scale: 1.6 },
    { name: "MIS Recycling", logo: CLIENT_LOGOS.misRecycling, scale: 1 },
    { name: "Nordic Bioscience", logo: CLIENT_LOGOS.nordicBioscience, scale: 1 },
    { name: "FirstFarms", logo: CLIENT_LOGOS.firstFarms, scale: 1 }
  ];

  // Double the array to ensure seamless loop
  const allClients = [...clients, ...clients];

  return (
    <section className="py-16 bg-slate-50 border-y border-slate-100" data-testid="logo-carousel-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <p className="text-center text-slate-500 font-inter text-sm uppercase tracking-wider">
          Udvalgte kunder
        </p>
      </div>
      <div className="overflow-hidden">
        <Marquee speed={40} gradient={false} pauseOnHover style={{ overflow: 'hidden' }}>
          {allClients.map((client, index) => (
            <div
              key={client.name}
              className="mx-3 flex-shrink-0"
              data-testid={`client-logo-${index}`}
            >
              <div className="flex items-center justify-center px-4 py-3 rounded-xl bg-white border border-slate-200 w-[190px] h-[85px] overflow-hidden">
                <img
                  src={client.logo}
                  alt={client.name}
                  className="object-contain"
                  style={{ transform: `scale(${client.scale})`, maxHeight: '75px', maxWidth: '170px' }}
                />
              </div>
            </div>
          ))}
        </Marquee>
      </div>
    </section>
  );
};

// AI Development Section
const AISection = () => {
  const steps = [
  { title: "Beskriv opgaven", text: "Skriv til AI-agenten hvad I vil have ændret i rapporter eller datamodel." },
  { title: "Se og test", text: "AI-agenten laver ændringen. I ser resultatet og tester det i udviklingsmiljøet." },
  { title: "Udgiv", text: "Når testene består og I har godkendt, udgiver I ændringen til produktion." }];


  const safety = [
  "Intet går live uden at tests består og et menneske har godkendt",
  "Fuld historik og rollback"];


  return (
    <AnimatedSection id="ai-udvikling" className="section-padding bg-white" data-testid="ai-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div variants={fadeUpVariant}>
            <p className="text-teal-600 font-manrope font-semibold text-sm uppercase tracking-wider mb-4">
              AI-assisteret udvikling
            </p>
            <h2 className="font-manrope font-bold text-3xl sm:text-4xl text-slate-900 mb-6">
              Jeres team udvikler selv BI med AI
            </h2>
            <p className="text-slate-600 text-lg mb-10">
              Med den AI-assisterede BI-platform venter I ikke på en konsulent.
              Jeres eget team laver ændringer i rapporter og datamodel via AI-agenten.
            </p>

            <div className="space-y-6 mb-10">
              {steps.map((step, index) =>
              <div key={step.title} className="flex items-start gap-4" data-testid={`ai-step-${index}`}>
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center">
                    <span className="text-teal-600 font-manrope font-bold">{index + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-manrope font-semibold text-slate-900 mb-1">{step.title}</h3>
                    <p className="text-slate-600 text-sm">{step.text}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 mb-8" data-testid="ai-safety-card">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="text-teal-600" size={20} />
                <h3 className="font-manrope font-semibold text-slate-900">Sikkerhed</h3>
              </div>
              <div className="space-y-3">
                {safety.map((item) =>
                <div key={item} className="flex items-start gap-3">
                    <CheckCircle className="text-teal-500 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-slate-600 text-sm">{item}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#booking"
                className="btn-primary inline-flex items-center justify-center gap-2 font-semibold px-8 py-4 rounded-lg"
                data-testid="ai-cta-booking">

                Book et møde og se AI-platformen
                <ArrowRight size={18} />
              </a>
            </div>
          </motion.div>

          <motion.div variants={fadeUpVariant}>
            <div className="relative">
              <div className="absolute -inset-4 bg-teal-500/20 rounded-2xl blur-2xl" />
              <video
                src={HERO_DEMO_VIDEO}
                poster={SALGSRAPPORT_IMG}
                autoPlay
                loop
                muted
                playsInline
                className="relative dashboard-image w-full"
                data-testid="ai-demo-video" />

            </div>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>);

};

// Templates Section (gratis Ultra Light templates)
const StarterPackagesSection = () => {
  const included = [
  "Forbinder direkte til Business Central eller e-conomic",
  "Salg, Finans, Projekt og Lager (kun BC)",
  "Kom i gang på under 30 minutter",
  "Mulighed for at tilkøbe klippekort på 10 timer til opsætning"];


  const limitations = [
  { title: "Ingen historik", text: "Du ser kun data som de ser ud lige nu." },
  { title: "Ingen rene definitioner", text: "Tallene beregnes direkte på rå ERP-data." },
  { title: "Ikke AI-parat", text: "Uden et data warehouse kan AI ikke arbejde sikkert med jeres datamodel." }];


  return (
    <AnimatedSection id="startpakker" className="section-padding bg-valubi-navy" data-testid="startpakker-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeUpVariant} className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-teal-400 font-manrope font-semibold text-sm uppercase tracking-wider mb-4">
            Kom i gang gratis
          </p>
          <h2 className="font-manrope font-bold text-3xl sm:text-4xl text-white mb-6">
            Gratis Power BI templates
          </h2>
          <p className="text-slate-300 text-lg">
            Vores Ultra Light templates forbinder direkte til Business Central eller e-conomic.
            Intet data warehouse. Tilmeld dig nyhedsbrevet og hent dem med det samme.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <motion.div
            variants={fadeUpVariant}
            id="ultralight"
            className="bg-white/5 rounded-2xl p-8 border-2 border-teal-400/40 flex flex-col scroll-mt-24"
            data-testid="templates-included-card">

            <h3 className="font-manrope font-bold text-2xl text-white mb-6">Det får du</h3>
            <div className="space-y-4 flex-grow">
              {included.map((item) =>
              <div key={item} className="flex items-start gap-3">
                  <CheckCircle className="text-teal-400 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-slate-300">{item}</span>
                </div>
              )}
            </div>
            <Link
              to="/skabeloner"
              className="mt-8 block text-center py-4 px-6 rounded-xl font-semibold transition-colors bg-teal-500 text-white hover:bg-teal-400"
              data-testid="templates-cta">

              Hent gratis templates
            </Link>
          </motion.div>

          <motion.div
            variants={fadeUpVariant}
            className="bg-white/5 rounded-2xl p-8 border border-white/10 flex flex-col"
            data-testid="templates-limitations-card">

            <h3 className="font-manrope font-bold text-2xl text-white mb-6">Det skal du vide</h3>
            <div className="space-y-5 flex-grow">
              {limitations.map((item) =>
              <div key={item.title} className="flex items-start gap-3">
                  <XCircle className="text-slate-500 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="font-semibold text-slate-200">{item.title}</p>
                    <p className="text-slate-400 text-sm">{item.text}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-8 p-4 bg-teal-500/10 rounded-xl border border-teal-400/30">
              <p className="text-slate-300 text-sm mb-3">
                Det er præcis dét, de betalte pakker løser.
              </p>
              <a
                href="#priser"
                className="inline-flex items-center gap-2 text-teal-300 font-semibold text-sm hover:text-teal-200 transition-colors"
                data-testid="templates-see-pricing-link">

                Se pakker og priser
                <ArrowRight size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>);

};

// Reports Section
const ReportsSection = () => {
  const [activeReport, setActiveReport] = useState(0);

  const reports = [
  {
    name: "Salgsrapport",
    icon: TrendingUp,
    image: SALGSRAPPORT_IMG,
    description: "Omsætning, dækningsbidrag og dækningsgrad fordelt på kunder, varer og perioder. Sammenlign med samme periode sidste år og år til dato.",
    features: [
    "Omsætning, dækningsbidrag og dækningsgrad",
    "Fordeling på kunder, varer og perioder",
    "Kreditnotaanalyse",
    "Kunde- og varechurn"]

  },
  {
    name: "Finansrapport",
    icon: Receipt,
    image: FINANSRAPPORT_IMG,
    description: "Resultatopgørelse, balance og cashflow baseret på dit eget layout i Excel.",
    features: [
    "Resultatopgørelse med realiseret, sidste år, afvigelse og index",
    "Balance med saldo, sidste år og bevægelse",
    "Cashflow med primo, bevægelser og ultimo",
    "Fleksibelt layout via Excel (du definerer kontosammensætning)",
    "Brug klippekortet til hjælp med opsætning"]

  },
  {
    name: "Lagerrapport",
    icon: Warehouse,
    image: LAGERRAPPORT_IMG,
    description: "Komplet overblik over lagerværdi, dødt lager og omsætningshastighed.",
    features: [
    "Lagerværdi fordelt på varer og lokationer",
    "Dødt lager – varer der ikke er solgt længe",
    "Omsætningshastighed per vare",
    "Lager i uger – hvor mange ugers salg er der lager til"]

  }];


  return (
    <AnimatedSection id="rapporter" className="section-padding bg-slate-50" data-testid="reports-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeUpVariant} className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-teal-600 font-manrope font-semibold text-sm uppercase tracking-wider mb-4">
            Inkluderet i de betalte pakker
          </p>
          <h2 className="font-manrope font-bold text-3xl sm:text-4xl text-slate-900 mb-6">
            Færdige Power BI rapporter
          </h2>
          <p className="text-slate-600 text-lg">
            Professionelle rapporter klar til brug fra dag ét. Se hvad der er inkluderet.
          </p>
        </motion.div>

        {/* Report Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {reports.map((report, index) =>
          <button
            key={report.name}
            onClick={() => setActiveReport(index)}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
            activeReport === index ?
            "bg-teal-600 text-white shadow-lg" :
            "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"}`
            }
            data-testid={`report-tab-${index}`}>

              <report.icon size={20} />
              {report.name}
            </button>
          )}
        </div>

        {/* Active Report */}
        <motion.div
          key={activeReport}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid lg:grid-cols-2 gap-12 items-center">

          <div className="order-2 lg:order-1">
            <h3 className="font-manrope font-bold text-2xl text-slate-900 mb-4">
              {reports[activeReport].name}
            </h3>
            <p className="text-slate-600 text-lg mb-8">
              {reports[activeReport].description}
            </p>
            <ul className="space-y-4">
              {reports[activeReport].features.map((feature) =>
              <li key={feature} className="flex items-start gap-3">
                  <CheckCircle className="text-teal-500 flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-700">{feature}</span>
                </li>
              )}
            </ul>
          </div>
          
          <div className="order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-teal-500/10 rounded-2xl blur-xl" />
              <img
                src={reports[activeReport].image}
                alt={reports[activeReport].name}
                className="relative rounded-xl shadow-2xl w-full"
                data-testid={`report-image-${activeReport}`} />

            </div>
          </div>
        </motion.div>
      </div>
    </AnimatedSection>);

};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
  {
    quote: "Vi har i 2025 fået Power BI. Vi fik Valubi til at lave selve opsætningen ud fra de ønsker vi havde. Det gik meget problemløst, og der var ikke mange rettelser i forhold til hvad vi forventede. De var også gode til at oplære os, så vi selv har kunnet lave nye udtræk m.m. Det er altid nemt at komme i kontakt med dem, og de er hurtige til at løse evt. problemer og nye ønsker.",
    name: "Maja Noermark Stefensen",
    title: "Økonomichef",
    company: "IBG A/S"
  },
  {
    quote: "Valubi har hjulpet os med at flytte vores Power BI rapporter efter vi skiftede til Business Central, og har løbende udviklet nye rapporter for os. Deres store viden om Business Central og hurtige reaktionstid har gjort processen effektiv. Vi forventer helt sikkert at bruge dem i fremtiden.",
    name: "Flemming Aarhus Pedersen",
    title: "Økonomidirektør / CFO",
    company: "Esbjerg Havn"
  },
  {
    quote: "Valubi har udført opgaver for os. Vi har oplevet god og professionel service samt hurtig levering.",
    name: "Christian Bruhn Gerdes",
    title: "Group Finance Controller",
    company: "FirstFarms A/S"
  }];


  return (
    <AnimatedSection id="testimonials" className="section-padding bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeUpVariant} className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-teal-600 font-manrope font-semibold text-sm uppercase tracking-wider mb-4">
            Kundernes ord
          </p>
          <h2 className="font-manrope font-bold text-3xl sm:text-4xl text-slate-900 mb-6">
            Det siger kunderne
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) =>
          <motion.div
            key={testimonial.name}
            variants={fadeUpVariant}
            className="bg-white rounded-xl p-8 border border-slate-100 testimonial-border card-hover"
            data-testid={`testimonial-card-${index}`}>

              <div className="quote-mark mb-4">"</div>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {testimonial.quote}
              </p>
              <div className="pt-6 border-t border-slate-100">
                <p className="font-manrope font-semibold text-slate-900">
                  {testimonial.name}
                </p>
                <p className="text-slate-500 text-sm">
                  {testimonial.title}
                </p>
                <p className="text-teal-600 font-medium text-sm">
                  {testimonial.company}
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </AnimatedSection>);

};

// Pricing Section
const PricingSection = () => {
  const packages = [
  {
    name: "Ultra Light templates",
    price: "Gratis",
    priceSub: "Power BI templates",
    features: [
    "Power BI templates direkte på ERP-data",
    "Intet data warehouse",
    "Salg, Finans, Projekt og Lager (kun BC)",
    "Kom i gang på under 30 minutter"],

    cta: "Hent gratis templates",
    href: "/skabeloner",
    recommended: false
  },
  {
    name: "Standard data warehouse",
    price: "20.000 / 30.000 kr.",
    priceSub: "e-conomic / Business Central. Fast pris.",
    features: [
    "Rigtigt data warehouse",
    "Standard rapportpakke: Salg, Lager, Finans, Projekt",
    "Rene, historiserede og konsistente data",
    "Vi laver ændringerne for jer"],

    cta: "Book et møde",
    href: "#booking",
    recommended: false
  },
  {
    name: "AI-assisteret BI-platform",
    price: "50.000 kr.",
    priceSub: "Fast pris, all-in",
    features: [
    "Alt i Standard data warehouse",
    "Selvbetjent udviklingsmiljø: udviklings- og produktionsmiljø plus CI/CD-pipeline",
    "AI-agent og en oplæringsdag",
    "Jeres eget team laver selv ændringer i rapporter og datamodel via AI-agenten: ser ændringen, tester, udgiver",
    "Intet går live uden at tests består og et menneske har godkendt",
    "Fuld historik og rollback",
    "AI-agenten ser datastrukturen, aldrig jeres data"],

    cta: "Book et møde",
    href: "#booking",
    recommended: true
  }];


  const addons = [
  {
    name: "Tryghedsabonnement",
    price: "3.000 kr./md.",
    icon: ShieldCheck,
    text: "Overvågning og reaktion inden for 24 timer på hverdage. Vi retter det der går i stykker. Nye ønsker faktureres separat."
  },
  {
    name: "Tilpasninger",
    price: "1.300 kr./time",
    icon: Zap,
    text: "Ny datakilde, større ombygninger eller custom opsætning. Vi kan aftale en fast pris på opgaven."
  }];


  return (
    <AnimatedSection id="priser" className="section-padding bg-valubi-navy" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeUpVariant} className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-teal-400 font-manrope font-semibold text-sm uppercase tracking-wider mb-4">
            Priser
          </p>
          <h2 className="font-manrope font-bold text-3xl sm:text-4xl text-white mb-6">
            Tre pakker. Fast pris.
          </h2>
          <p className="text-slate-300 text-lg">
            Start gratis med templates. Vælg data warehouse når I vil have rene data. Vælg platformen når I selv vil eje jeres BI.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">
          {packages.map((pkg, index) =>
          <motion.div
            key={pkg.name}
            variants={fadeUpVariant}
            className={`relative rounded-2xl p-8 flex flex-col ${
            pkg.recommended ?
            "bg-teal-500/10 border-2 border-teal-400/60 shadow-[0_0_40px_rgba(45,212,191,0.15)]" :
            "bg-white/5 border border-white/10"}`
            }
            data-testid={`pricing-package-${index}`}>

              {pkg.recommended &&
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-teal-500 text-white text-sm font-manrope font-semibold rounded-full whitespace-nowrap">
                    <Sparkles size={14} />
                    Anbefalet
                  </span>
                </div>
            }

              <h3 className="font-manrope font-bold text-xl text-white mb-4">{pkg.name}</h3>
              <div className="mb-1">
                <span className={`font-manrope font-bold text-3xl ${pkg.recommended ? "text-teal-300" : "text-white"}`}>
                  {pkg.price}
                </span>
              </div>
              <p className="text-slate-400 text-sm mb-6">{pkg.priceSub}</p>

              <div className="space-y-3 flex-grow mb-8">
                {pkg.features.map((feature) =>
              <div key={feature} className="flex items-start gap-3">
                    <CheckCircle className="text-teal-400 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </div>
              )}
              </div>

              {pkg.href.startsWith("#") ?
            <a
              href={pkg.href}
              className={`block text-center py-3.5 px-6 rounded-xl font-semibold transition-colors ${
              pkg.recommended ?
              "bg-teal-500 text-white hover:bg-teal-400" :
              "bg-white/10 text-white hover:bg-white/20"}`
              }
              data-testid={`pricing-cta-${index}`}>

                  {pkg.cta}
                </a> :

            <Link
              to={pkg.href}
              className="block text-center py-3.5 px-6 rounded-xl font-semibold transition-colors bg-white/10 text-white hover:bg-white/20"
              data-testid={`pricing-cta-${index}`}>

                  {pkg.cta}
                </Link>
            }
            </motion.div>
          )}
        </div>

        <motion.div variants={fadeUpVariant} className="mt-10 max-w-3xl mx-auto text-center">
          <p className="text-teal-300 font-medium">
            Springet fra data warehouse til AI-platformen er kun 20.000 kr. ekstra. Så ejer jeres team selv jeres BI.
          </p>
        </motion.div>

        <motion.div variants={fadeUpVariant} className="mt-16 max-w-4xl mx-auto">
          <h3 className="font-manrope font-bold text-xl text-white text-center mb-8">Tillæg</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {addons.map((addon, index) =>
            <div
              key={addon.name}
              className="bg-white/5 rounded-2xl p-6 border border-white/10"
              data-testid={`pricing-addon-${index}`}>

                <div className="flex items-center gap-4 mb-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-teal-500/20 flex-shrink-0">
                    <addon.icon className="text-teal-400" size={24} />
                  </div>
                  <div>
                    <h4 className="font-manrope font-bold text-lg text-white">{addon.name}</h4>
                    <p className="font-manrope font-bold text-teal-300">{addon.price}</p>
                  </div>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">{addon.text}</p>
              </div>
            )}
          </div>
        </motion.div>

        <motion.div variants={fadeUpVariant} className="mt-12 text-center">
          <p className="text-slate-300 max-w-2xl mx-auto">
            Alle priser er ekskl. moms.
          </p>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// About Section
const AboutSection = () => {
  return (
    <AnimatedSection id="about" className="section-padding bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div variants={fadeUpVariant} className="order-2 lg:order-1">
            <p className="text-teal-600 font-manrope font-semibold text-sm uppercase tracking-wider mb-4">
              Om mig
            </p>
            <h2 className="font-manrope font-bold text-3xl sm:text-4xl text-slate-900 mb-6">
              Jonathan Midjord Jørgensen
            </h2>
            <p className="text-slate-600 text-lg mb-6">
              Jeg har arbejdet med business intelligence og data warehousing i 10+ år, 
              og har en fortid som Business Central konsulent.
            </p>
            <p className="text-slate-600 mb-6">
              Den kombination giver mig en dyb forståelse for hvordan ERP-data hænger sammen, 
              og hvordan man bygger rapporteringsløsninger der faktisk giver værdi for økonomifunktionen.
            </p>
            <p className="text-slate-600 mb-8">
              Jeg arbejder med faste priser fordi jeg mener, at det er den mest gennemsigtige og fair måde at arbejde på. Det giver dig tryghed og forudsigelighed.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <div className="px-4 py-2 bg-slate-50 rounded-lg border border-slate-200">
                <span className="text-slate-500 text-sm">Erfaring:</span>
                <span className="text-slate-900 font-medium ml-2">10+ år</span>
              </div>
              <div className="px-4 py-2 bg-slate-50 rounded-lg border border-slate-200">
                <span className="text-slate-500 text-sm">Baggrund:</span>
                <span className="text-slate-900 font-medium ml-2">BC Konsulent</span>
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeUpVariant} className="order-1 lg:order-2">
            <img
              src={FOUNDER_IMG}
              alt="Jonathan Midjord Jørgensen"
              className="w-full max-w-md mx-auto rounded-2xl shadow-lg"
              data-testid="founder-image" />
          </motion.div>
        </div>
      </div>
    </AnimatedSection>);

};

// Booking Section with HubSpot Calendar
const BookingSection = () => {
  useEffect(() => {
    // Load HubSpot meetings script
    const existingScript = document.querySelector('script[src*="MeetingsEmbedCode"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  return (
    <AnimatedSection id="booking" className="section-padding cta-gradient" data-testid="booking-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeUpVariant} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 rounded-full mb-6">
            <Calendar className="text-teal-400" size={18} />
            <span className="text-teal-400 font-medium text-sm">Gratis og uforpligtende</span>
          </div>
          <h2 className="font-manrope font-bold text-3xl sm:text-4xl text-white mb-6">
            Book et møde
          </h2>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            Lad os tale om dine behov og se om templates, data warehouse eller AI-platformen passer til dig.
          </p>
        </motion.div>

        <motion.div
          variants={fadeUpVariant}
          className="bg-white rounded-2xl p-4 sm:p-8 shadow-2xl">

          <div
            className="meetings-iframe-container"
            data-src="https://meetings.hubspot.com/jonathan-midjord-joergensen/halv-times-sparring?embed=true"
            data-testid="hubspot-calendar" />

        </motion.div>
      </div>
    </AnimatedSection>);

};

// Contact Section with Callback Form
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    company: '',
    source_systems: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    if (!formData.name || !formData.phone || !formData.company) {
      setSubmitStatus({ type: 'error', message: 'Udfyld venligst alle felter' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          source_systems: formData.source_systems.trim() ? [formData.source_systems.trim()] : []
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus({ type: 'success', message: data.message });
        setFormData({ name: '', phone: '', company: '', source_systems: '' });
      } else {
        setSubmitStatus({ type: 'error', message: data.detail || 'Der opstod en fejl. Prøv igen.' });
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus({ type: 'error', message: 'Der opstod en fejl. Prøv igen eller ring til os.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatedSection id="contact" className="section-padding bg-slate-50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <motion.div variants={fadeUpVariant}>
            <p className="text-teal-600 font-manrope font-semibold text-sm uppercase tracking-wider mb-4">
              Bliv ringet op
            </p>
            <h2 className="font-manrope font-bold text-3xl sm:text-4xl text-slate-900 mb-6">
              Vi ringer til dig
            </h2>
            <p className="text-slate-600 text-lg mb-8">
              Udfyld formularen, så ringer vi dig op for en uforpligtende snak om dine muligheder.
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200">
                <div className="icon-container flex-shrink-0">
                  <Phone className="text-white" size={20} />
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Eller ring direkte</p>
                  <a href="tel:+4561699916" className="font-manrope font-bold text-lg text-slate-900 hover:text-teal-600 transition-colors">
                    61 69 99 16
                  </a>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="text-teal-500 flex-shrink-0 mt-0.5" size={20} />
                <span className="text-slate-600">Vi ringer inden for 24 timer på hverdage</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="text-teal-500 flex-shrink-0 mt-0.5" size={20} />
                <span className="text-slate-600">Uforpligtende samtale</span>
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeUpVariant}>
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
              {submitStatus &&
              <div className={`mb-6 p-4 rounded-lg ${
              submitStatus.type === 'success' ?
              'bg-teal-50 text-teal-700 border border-teal-200' :
              'bg-red-50 text-red-700 border border-red-200'}`
              }>
                  {submitStatus.message}
                </div>
              }

              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    Dit navn *
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="Dit fulde navn"
                    data-testid="callback-name-input" />

                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                    Telefonnummer *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="Dit telefonnummer"
                    data-testid="callback-phone-input" />

                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                    Virksomhed *
                  </label>
                  <input
                    type="text"
                    id="company"
                    value={formData.company}
                    onChange={(e) => setFormData((prev) => ({ ...prev, company: e.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="Virksomhedens navn"
                    data-testid="callback-company-input" />

                </div>

                <div>
                  <label htmlFor="source_systems" className="block text-sm font-medium text-slate-700 mb-2">
                    Kildesystemer <span className="text-slate-400 font-normal">(f.eks. Business Central, e-conomic)</span>
                  </label>
                  <input
                    type="text"
                    id="source_systems"
                    value={formData.source_systems}
                    onChange={(e) => setFormData((prev) => ({ ...prev, source_systems: e.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="F.eks. Business Central, Excel"
                    data-testid="callback-source-systems-input" />

                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 px-6 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="callback-submit-button">

                  {isSubmitting ?
                  <>
                      <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      Sender...
                    </> :

                  <>
                      <Phone size={20} />
                      Ring mig op
                    </>
                  }
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>);

};

// Footer
const Footer = () => {
  return (
    <footer className="bg-valubi-navy py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Valubi" className="h-8 w-auto" />
            <span className="text-slate-400 text-sm">
              © {new Date().getFullYear()} Valubi ApS. Alle rettigheder forbeholdes.
            </span>
          </div>
          
          <div className="flex items-center gap-6">
            <a href="#startpakker" className="text-slate-400 hover:text-white text-sm transition-colors">
              Templates
            </a>
            <a href="#priser" className="text-slate-400 hover:text-white text-sm transition-colors">
              Priser
            </a>
            <a href="#about" className="text-slate-400 hover:text-white text-sm transition-colors">
              Om mig
            </a>
            <a href="tel:+4561699916" className="text-slate-400 hover:text-white text-sm transition-colors">
              61 69 99 16
            </a>
          </div>
        </div>
      </div>
    </footer>);

};

// Cookie Consent Banner & HubSpot Tracking
const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    // Check if consent was already given
    const savedConsent = localStorage.getItem('valubi_cookie_consent');
    if (savedConsent === null) {
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      setConsent(savedConsent === 'true');
      if (savedConsent === 'true') {
        loadHubSpotTracking();
      }
    }
  }, []);

  const loadHubSpotTracking = () => {
    // Only load if not already loaded
    if (!document.getElementById('hs-script-loader')) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.id = 'hs-script-loader';
      script.async = true;
      script.defer = true;
      script.src = '//js.hs-scripts.com/47937401.js';
      document.body.appendChild(script);
    }
  };

  const handleAccept = () => {
    localStorage.setItem('valubi_cookie_consent', 'true');
    setConsent(true);
    setShowBanner(false);
    loadHubSpotTracking();
  };

  const handleDecline = () => {
    localStorage.setItem('valubi_cookie_consent', 'false');
    setConsent(false);
    setShowBanner(false);
  };

  return (
    <AnimatePresence>
      {showBanner &&
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        data-testid="cookie-consent-banner">

          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                  <Cookie className="text-teal-600" size={24} />
                </div>
              </div>
              <div className="flex-grow">
                <h3 className="font-manrope font-bold text-lg text-slate-900 mb-2">
                  Vi bruger cookies
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Vi bruger cookies til at forbedre din oplevelse på hjemmesiden og til at analysere trafik. 
                  Ved at klikke "Acceptér" giver du samtykke til brug af cookies.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                <button
                onClick={handleDecline}
                className="px-6 py-3 text-slate-600 font-medium rounded-xl border border-slate-200 hover:bg-slate-50 transition-all"
                data-testid="cookie-decline-button">

                  Afvis
                </button>
                <button
                onClick={handleAccept}
                className="px-6 py-3 bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-700 transition-all"
                data-testid="cookie-accept-button">

                  Acceptér
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      }
    </AnimatePresence>);

};

// Main App Component
function HomePage() {
  const location = useLocation();

  // When arriving at "/" with a hash (e.g. clicking "Startpakker" from /blog),
  // scroll to that section once the home page mounts.
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      // Wait a tick so AnimatedSection content is rendered
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [location]);

  return (
    <div className="App">
      <SEO
        title="Valubi | Power BI, data warehouse og AI-assisteret BI til fast pris"
        description="Power BI, data warehouse og AI-assisteret BI til Business Central og e-conomic. Til fast pris. Start gratis med vores Power BI templates."
        path="/" />

      <Navigation />
      <HeroSection />
      <LogoCarousel />
      <AISection />
      <StarterPackagesSection />
      <ReportsSection />
      <TestimonialsSection />
      <PricingSection />
      <AboutSection />
      <BookingSection />
      <ContactSection />
      <Footer />
      <CookieConsent />
    </div>);

}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/skabeloner" element={<TemplatesPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
      </Routes>
      <NewsletterPopup />
    </BrowserRouter>);

}

export default App;